/**
 * Anonymous Name Generator
 *
 * Generates fun, memorable anonymous names like "Brave Fox 432"
 */

const ADJECTIVES = [
  'Brave', 'Swift', 'Bold', 'Calm', 'Clever', 'Fierce', 'Gentle', 'Happy',
  'Jolly', 'Kind', 'Lucky', 'Mighty', 'Noble', 'Quick', 'Proud', 'Silent',
  'Steady', 'Sunny', 'True', 'Wise', 'Agile', 'Bright', 'Daring', 'Eager',
  'Free', 'Golden', 'Humble', 'Iron', 'Jade', 'Keen', 'Lively', 'Merry',
  'Nimble', 'Orange', 'Peaceful', 'Radiant', 'Serene', 'Tender', 'United', 'Vivid',
]

const ANIMALS = [
  'Fox', 'Wolf', 'Bear', 'Eagle', 'Hawk', 'Owl', 'Deer', 'Rabbit',
  'Otter', 'Badger', 'Raven', 'Falcon', 'Lion', 'Tiger', 'Panda', 'Koala',
  'Dolphin', 'Whale', 'Shark', 'Turtle', 'Crane', 'Swan', 'Dove', 'Finch',
  'Sparrow', 'Robin', 'Jaguar', 'Leopard', 'Lynx', 'Moose', 'Elk', 'Bison',
  'Coyote', 'Gecko', 'Iguana', 'Mantis', 'Beetle', 'Moth', 'Firefly', 'Cricket',
]

/**
 * Generate a random anonymous name
 * Format: "Adjective Animal 123"
 */
export function generateAnonName(): string {
  const adjective = ADJECTIVES[Math.floor(Math.random() * ADJECTIVES.length)] ?? 'Brave'
  const animal = ANIMALS[Math.floor(Math.random() * ANIMALS.length)] ?? 'Fox'
  const number = Math.floor(Math.random() * 900) + 100 // 100-999

  return `${adjective} ${animal} ${number}`
}

/**
 * Generate a shorter anonymous name (for display in tight spaces)
 * Format: "A. Animal"
 */
export function generateShortAnonName(): string {
  const adjective = ADJECTIVES[Math.floor(Math.random() * ADJECTIVES.length)] ?? 'Brave'
  const animal = ANIMALS[Math.floor(Math.random() * ANIMALS.length)] ?? 'Fox'

  return `${adjective[0]}. ${animal}`
}

/**
 * Get initials from an anonymous name
 * "Brave Fox 432" -> "BF"
 */
export function getNameInitials(name: string): string {
  const words = name.split(' ').filter(w => isNaN(Number(w)))
  return words.map(w => w[0]).join('').toUpperCase().slice(0, 2)
}

/**
 * Check if a name looks like an auto-generated one
 */
export function isGeneratedName(name: string): boolean {
  const parts = name.split(' ')
  if (parts.length !== 3) return false

  const adj = parts[0]
  const animal = parts[1]
  const num = parts[2]

  if (!adj || !animal || !num) return false

  return (
    ADJECTIVES.includes(adj) &&
    ANIMALS.includes(animal) &&
    !isNaN(Number(num))
  )
}
